'use client'

import React, { createContext, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { PageHeader, Tag, type PageHeaderProps } from '@kiao-voice/ui-kit'
import type { OrganizationKind } from '@kiao-voice/core'

export interface WorkspaceIndicatorValue {
  key: string
  name: string
  mode: OrganizationKind
}

const WorkspaceIndicatorContext = createContext<WorkspaceIndicatorValue | null>(null)

export function WorkspaceIndicatorProvider({
  value,
  children,
}: {
  value: WorkspaceIndicatorValue | null
  children: React.ReactNode
}) {
  return (
    <WorkspaceIndicatorContext.Provider value={value}>
      {children}
    </WorkspaceIndicatorContext.Provider>
  )
}

/** The workspace the shell resolved, or null outside the shell and while it resolves. */
export function useWorkspaceIndicator(): WorkspaceIndicatorValue | null {
  return useContext(WorkspaceIndicatorContext)
}

export function WorkspaceChip() {
  const workspace = useContext(WorkspaceIndicatorContext)
  const { t } = useTranslation('web')
  if (!workspace) return null
  return (
    <Tag
      className="shrink-0 normal-case tracking-normal"
      aria-label={t('appShell.workspaceChipLabel', { name: workspace.name })}
      data-testid="workspace-chip"
    >
      {workspace.name}
    </Tag>
  )
}

export function WorkspacePageHeader({ actions, ...props }: PageHeaderProps) {
  const workspace = useContext(WorkspaceIndicatorContext)
  if (!workspace) return <PageHeader {...props} actions={actions} />
  return (
    <PageHeader
      {...props}
      actions={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <WorkspaceChip />
          {actions}
        </div>
      }
    />
  )
}
